if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        // Exclude custom upholstery style picker from option values
        return Array.from(this.querySelectorAll('select option[selected], fieldset:not([data-upholstery-style-picker]) input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      /**
       * Check if the event target is from the custom upholstery style picker
       */
      isCustomUpholsteryPicker(target) {
        if (!target) return false;
        // Check if target itself has the data attribute
        if (target.hasAttribute && target.hasAttribute('data-upholstery-style')) return true;
        // Check if target is inside a custom picker fieldset
        const fieldset = target.closest('fieldset[data-upholstery-style-picker]');
        return !!fieldset;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          // CRITICAL: Ignore change events from the custom upholstery style picker
          // The custom picker should NEVER trigger the standard variant selection flow
          if (this.isCustomUpholsteryPicker(event.target)) {
            // Stop propagation just in case
            event.stopPropagation();
            return;
          }

          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });

        // Auto-select first visible option if the currently selected one is hidden
        this.autoSelectFirstVisibleOption();
      }

      /**
       * Checks all fieldsets and select elements for hidden selected options.
       * If a selected option is hidden (unavailable), automatically selects the first visible option.
       */
      autoSelectFirstVisibleOption() {
        // Skip if this component has already processed auto-selection
        if (this.hasAttribute('data-auto-selected')) return;
        
        let needsUpdate = false;
        let lastChangedInput = null;

        // Handle fieldsets (radio buttons for swatch, color, button picker types)
        // Exclude the custom upholstery style picker as it's not a real variant option
        this.querySelectorAll('fieldset.product-form__input:not([data-upholstery-style-picker])').forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          if (!checkedInput) return;

          // Check if the checked input is inside a hidden wrapper
          const wrapper = checkedInput.closest('.variant-option-wrapper');
          const isHidden = wrapper && wrapper.classList.contains('hidden');

          if (isHidden) {
            // Find the first visible input in this fieldset
            const firstVisibleWrapper = fieldset.querySelector('.variant-option-wrapper:not(.hidden)');
            if (firstVisibleWrapper) {
              const firstVisibleInput = firstVisibleWrapper.querySelector('input[type="radio"]');
              if (firstVisibleInput) {
                // Uncheck the hidden input and check the visible one
                checkedInput.checked = false;
                firstVisibleInput.checked = true;
                needsUpdate = true;
                lastChangedInput = firstVisibleInput;

                // Update the selected value display
                const selectedValueDisplay = fieldset.querySelector('[data-selected-value], [data-selected-swatch-value]');
                if (selectedValueDisplay) {
                  selectedValueDisplay.innerHTML = firstVisibleInput.value;
                }
              }
            }
          }
        });

        // Handle select elements (dropdown picker types)
        this.querySelectorAll('select').forEach((select) => {
          const selectedOption = select.querySelector('option[selected]');
          if (!selectedOption) return;

          // Check if the selected option is hidden/disabled
          const isHidden = selectedOption.hidden || selectedOption.disabled;

          if (isHidden) {
            // Find the first visible (not hidden and not disabled) option
            const firstVisibleOption = select.querySelector('option:not([hidden]):not([disabled])');
            if (firstVisibleOption) {
              // Remove selected from current and set on first visible
              selectedOption.removeAttribute('selected');
              firstVisibleOption.setAttribute('selected', 'selected');
              select.value = firstVisibleOption.value;
              needsUpdate = true;
              lastChangedInput = select;

              // Update the swatch display if it exists
              const swatchValue = firstVisibleOption.dataset.optionSwatchValue;
              const selectedDropdownSwatchValue = select
                .closest('.product-form__input')
                ?.querySelector('[data-selected-value] > .swatch');
              if (selectedDropdownSwatchValue) {
                if (swatchValue) {
                  selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
                  selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
                } else {
                  selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
                  selectedDropdownSwatchValue.classList.add('swatch--unavailable');
                }
              }
            }
          }
        });

        // If we made any changes, trigger a change event to update the product info
        if (needsUpdate && lastChangedInput) {
          // Mark this component as having processed auto-selection to prevent loops
          this.setAttribute('data-auto-selected', 'true');
          
          // Use setTimeout to ensure the DOM has fully updated
          setTimeout(() => {
            // Verify the component is still connected before publishing
            if (!this.isConnected) return;
            
            const target = this.getInputForEventTarget(lastChangedInput);
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event: { target: lastChangedInput },
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            });
          }, 0);
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
